import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { ErrorBoundary } from './components/error-boundary';
import { ConfirmDialogProvider } from './components/ui/confirm-dialog';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/toaster';
import { reloadForNewVersion } from './lib/chunk-reload';
import { installErrorLog } from './lib/error-log';
import { queryClient } from './lib/query-client';
// Self-host Plus Jakarta Sans (the viewer's primary font) so it renders
// correctly OFFLINE — the index.html Google Fonts <link> can't load without
// internet at a public-event kiosk. Bundled by Vite, works online + offline.
import '@fontsource-variable/plus-jakarta-sans';
import './index.css';
// The new kit's tokens and component looks. Inert until <html> carries
// `data-kit="next"`, which `useKitScope` sets on the tool routes only.
import './styles/kit-next.css';

// Note: an earlier version of this file patched
// `HTMLImageElement.prototype.crossOrigin` as a defensive workaround for
// users whose networks stripped CORS headers from direct R2 responses.
// That problem is now solved at a different layer — `public.service.ts`
// rewrites every media URL through `/public/r2-proxy`, so the browser
// only sees same-host requests to the API (which has working CORS) and
// never has to talk to *.r2.cloudflarestorage.com directly.
//
// With the proxy in place we keep `crossOrigin='anonymous'` on every
// <img> (the Three.js default, plus our own prefetches), the API replies
// with the right ACAO header, the WebGL canvas stays untainted, and the
// The crossfade helper can snapshot the live frame instantaneously
// instead of waiting for a network-fetched preview URL.

// Vite fires this when a lazy chunk (or its CSS) fails to load — after a
// deploy, the old build's files are gone. Reloading fetches the new build;
// `preventDefault` stops the error from reaching the error screen. When the
// reload already happened moments ago it lets the error through instead.
window.addEventListener('vite:preloadError', event => {
  if (reloadForNewVersion()) event.preventDefault();
});

installErrorLog();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

// NOTE: StrictMode is intentionally disabled. PSV (Photo Sphere Viewer) is
// an imperative Three.js wrapper that does NOT tolerate React's dev-only
// double-invocation pattern: every mount the constructor kicks off a
// network fetch for the panorama texture; the immediate unmount aborts
// it; the re-mount fires the same fetch again. In practice this leaves
// PSV in a state where its first paint never settles and the viewer
// stays black indefinitely. StrictMode's checks are useful but they
// cost more than they buy here. Re-enable once we migrate to a
// declarative wrapper that's safe to mount twice.
// Router basename derives from Vite's configured base (`import.meta.env.BASE_URL`).
// On the normal web build this is '/' (no-op). When bundled into the offline
// Tauri shell under `/urbania360-app/` (VITE_BASE), react-router must strip that
// prefix so history routes like `/urbania360-app/t/:publicId` still match
// `/t/:publicId`. BASE_URL carries a trailing slash; BrowserRouter wants none.
const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || undefined;

createRoot(rootEl).render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter basename={routerBasename}>
      <ErrorBoundary>
        <TooltipProvider delayDuration={300} skipDelayDuration={300}>
          <ConfirmDialogProvider>
            <App />
            {/* Below the 56px top bar (h-14) + a 16px gap, so a toast never covers the account menu. */}
            <Toaster position="top-right" offset={{ top: 72, right: 16 }} mobileOffset={{ top: 64, right: 12 }} />
          </ConfirmDialogProvider>
        </TooltipProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </QueryClientProvider>,
);
